import type { ThreeDRenderModule } from '@/lib/three-d-resources/three-d-render-module'
import type { DeepReadonly, Ref } from 'vue'
import { clone } from 'ramda'
import {
  BufferGeometry,
  FrontSide,
  Group,
  Line,
  LineBasicMaterial,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  NoBlending,
  Object3D,
  Quaternion,
  Vector3,
  TextureLoader,
} from 'three'
import type { BufferAttribute, Camera, PerspectiveCamera, Scene, Material, Texture } from 'three'
import type { DrillFile } from '~/entities/drill'
import type { CoreImageApiData, DrillDataSet, DrillLabel, DrillLayer, DrillLabelApiData, DrillLabelConfig } from '@/entities/drillLabel'
import type { ModelView } from '~/entities/meeting'
import type { DrillLabelVisibility } from '~/entities/model3d'
import { rectangleRounded } from '~/lib/three-d-editor/utils'
import { applyLocalization } from '@/lib/three-d-resources/geometry/localization'
import { DEFAULT_FIXED_SIZE, DEFAULT_FONT_SIZE } from '../constants/labels'
import { getVertex } from './geometry/utils'
import { createMeshThumbnailMaterial, groupDataBy, getDrillLabelName } from '~/lib/three-d-resources/utils'
import type { DrillLayerService } from './drill-data-set-manager'
import DataCache from '~/lib/data-cache'

const SIG_INTERCEPT_WIDTH = 52
const SIG_INTERCEPT_HEIGHT = 40
const TITLE_OVERLAY_HEIGHT = 70
const CONTROLS_TRANSITION = 150
const DRILL_LABEL_FADE_IN_DELAY = 10
const DRILL_LABEL_TRANSITION = 500

export const clustersColorMapping = [
  { name: 'Alpha', color: 0xff2200, colorName: 'Red' },
  { name: 'Beta', color: 0x32cd32, colorName: 'Lime Green' },
  { name: 'Gamma', color: 0xff8000, colorName: 'Orange' },
  { name: 'Delta', color: 0xff1493, colorName: 'Deep Pink' },
  { name: 'Epsilon', color: 0xffff54, colorName: 'Lemon' },
  { name: 'Zeta', color: 0x40e0d0, colorName: 'Turquoise' },
  { name: 'Eta', color: 0xba55d3, colorName: 'Medium Orchid' },
  { name: 'Theta', color: 0xffdab9, colorName: 'Peach Puff' },
  { name: 'Iota', color: 0xffffe0, colorName: 'Light Yellow' },
  { name: 'Kappa', color: 0x00ff00, colorName: 'Green' },
  { name: 'Lambda', color: 0xff69b4, colorName: 'Hot Pink' },
  { name: 'Mu', color: 0x00ced1, colorName: 'Dark Turquoise' },
  { name: 'Nu', color: 0x8a2be2, colorName: 'Blue Violet' },
  { name: 'Xi', color: 0xffb800, colorName: 'Dark Orange' },
  { name: 'Omicron', color: 0x3cb371, colorName: 'Medium Sea Green' },
  { name: 'Pi', color: 0xff6347, colorName: 'Tomato' },
  { name: 'Rho', color: 0x33d7ff, colorName: 'Light Sky Blue' },
  { name: 'Sigma', color: 0xff33f6, colorName: 'Pink' },
  { name: 'Tau', color: 0xffd700, colorName: 'Gold' },
  { name: 'Upsilon', color: 0xcd327c, colorName: 'Medium Violet Red' },
  { name: 'Phi', color: 0xc3ebc3, colorName: 'Light Green' },
  { name: 'Chi', color: 0xacb2f1, colorName: 'Light Steel Blue' },
]

function createTextLabel() {
  const textLabel = document.createElement('div')
  textLabel.dataset.testid = 'drill-label-text-label'
  textLabel.style.fontSize = `${DEFAULT_FONT_SIZE}px`
  textLabel.style.borderRadius = '4px'
  textLabel.style.backgroundColor = '#000000'
  textLabel.style.color = '#ffffff'
  textLabel.style.display = 'flex'
  textLabel.style.flexDirection = 'column'
  textLabel.style.lineHeight = '1.5em'
  textLabel.style.position = 'relative'
  textLabel.style.visibility = 'visible'
  textLabel.style.maxWidth = '50em'
  return textLabel
}

function createTextTitle(drillLabel: DrillLabelApiData) {
  const textTitle = document.createElement('div')
  textTitle.dataset.testid = 'drill-label-text-title'
  textTitle.style.fontWeight = '700'
  textTitle.style.padding = '0.125em 0.1875em 0.125em 0.375em'
  textTitle.style.whiteSpace = 'nowrap'
  textTitle.style.display = 'flex'
  textTitle.style.justifyContent = 'space-between'

  const holeId = document.createElement('div')
  holeId.style.display = 'flex'
  holeId.style.alignItems = 'center'
  holeId.style.gap = '0.5em'

  holeId.prepend(drillLabel.clusterData ? clustersColorMapping[drillLabel.clusterData.cluster_id].name : drillLabel.hole_id)
  textTitle.appendChild(holeId)

  if (drillLabel.coreImages.length > 0) {
    const imgIcon = document.createElement('span')
    imgIcon.style.height = '50%'
    imgIcon.style.display = 'flex'
    imgIcon.style.alignItems = 'center'
    imgIcon.innerHTML = getImageSvg()

    setTimeout(() => {
      const textTitleFontSize = getComputedStyle(textTitle).fontSize
      const svgIcon = imgIcon.querySelector('svg')

      if (textTitleFontSize && svgIcon) {
        svgIcon?.setAttribute('width', textTitleFontSize)
        svgIcon?.setAttribute('height', textTitleFontSize)
      }
    }, 0)

    holeId.appendChild(imgIcon)
  }

  return textTitle
}

function createCollapsedTextBody(this: any, drillLabel: DrillLabelApiData) {
  const textBody = _createTextBody()
  textBody.classList.add('collapsed-content')

  const inlineEditable = _createInlineEditable.call(
    this,
    'edited_collapsed_intercept_info',
    drillLabel.edited_collapsed_intercept_info ?? drillLabel.collapsed_intercept_info,
  )

  textBody.append(inlineEditable)

  return textBody
}

function createFullTextBody(this: any, drillLabel: DrillLabelApiData) {
  const textBody = _createTextBody()
  textBody.classList.add('expanded-content')

  const expandedEditable = _createInlineEditable.call(
    this,
    'edited_expanded_intercept_info',
    drillLabel.edited_expanded_intercept_info ?? drillLabel.expanded_intercept_info,
  )
  const subEditable = _createInlineEditable.call(
    this,
    'edited_sub_intercept_info',
    drillLabel.edited_sub_intercept_info ?? drillLabel.sub_intercept_info,
  )

  textBody.append(expandedEditable, subEditable)

  return textBody
}

function _createInlineEditable(this: any, interceptInfoKey: string, textContent: string) {
  const inlineEditable = document.createElement('div')
  inlineEditable.classList.add('inline-editable')

  const isMultiline = (textarea: string) => textarea.includes('\n')
  const input = document.createElement('textarea')
  input.style.display = 'none'
  input.value = textContent

  const clickableInput = document.createElement('div')
  clickableInput.classList.add('clickable-input', interceptInfoKey)
  clickableInput.textContent = textContent
  const threeDElements = document.querySelector('.threed-elements')

  let inputRoot: Node | null
  let oldSibling: Node | null

  clickableInput.addEventListener('click', () => {
    if (this.mode !== 'edit') return

    clickableInput.style.display = 'none'
    input.value = clickableInput.textContent ?? ''
    inputRoot = input.closest('.drill-label-label-div')!
    oldSibling = inputRoot?.previousSibling

    // move the drill label to the end of threed-elements div so it appears above the topology
    // give it the data-threed-entity attribute so it can be selected by drill label css
    if (inputRoot) {
      threeDElements!.parentNode!.appendChild(inputRoot)
      threeDElements!.setAttribute('data-threed-entity', 'drill-label')
    }

    input.style.display = 'block'
    if (isMultiline(input.value)) input.style.whiteSpace = 'normal'

    setTimeout(() => {
      input.focus()
      input.setSelectionRange(input.value.length, input.value.length)
    }, 0)
  })

  const switchToDiv = () => {
    clickableInput.style.display = 'block'
    input.style.display = 'none'

    if (input.value && input.value !== clickableInput.textContent) {
      clickableInput.textContent = input.value
      this.updateDrillLabelApiData(Number(inlineEditable.parentElement?.dataset.drillId), {
        [interceptInfoKey]: input.value,
      })
      this.onLabelsChanged()
    }

    setTimeout(() => {
      // move the drill label back to its original position and remove the data-threed-entity attribute
      if (oldSibling?.parentNode && inputRoot) {
        oldSibling.parentNode?.insertBefore(inputRoot, oldSibling.nextSibling)
        threeDElements?.removeAttribute('data-threed-entity')
      }
    }, 0)
  }

  input.addEventListener('blur', switchToDiv)
  input.addEventListener('keydown', event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      switchToDiv()
    }
  })

  // SPACEGHOST using this in an attempt to be clever and get scaling to be smoother
  // this is a hidden div that will have the same text as the input and will be used
  // to calculate the width of the input
  const spaceghost = document.createElement('div')
  spaceghost.setAttribute('aria-hidden', 'true')
  spaceghost.style.height = '1px'
  spaceghost.style.visibility = 'hidden'
  spaceghost.style.padding = '0 0.325em'
  spaceghost.style.overflowY = 'hidden'
  spaceghost.style.overflowX = 'visible'
  spaceghost.innerText = input.value
  spaceghost.style.display = 'inline-flex'
  spaceghost.style.alignSelf = 'flex-start'
  spaceghost.style.fontSize = input.style.fontSize

  const resizeInput = (event: Event) => {
    const target = event.target as HTMLTextAreaElement
    let lines = target.value.split(/\r\n|\r|\n/).length

    target.setAttribute('rows', lines.toString())
    spaceghost.innerText = target.value
    this._container?.style.setProperty('--drill-label-width', `${spaceghost.offsetWidth}px`)
  }

  input.addEventListener('input', resizeInput)
  input.addEventListener('focus', resizeInput)

  inlineEditable.appendChild(input)
  inlineEditable.appendChild(clickableInput)
  inlineEditable.appendChild(spaceghost)

  return inlineEditable
}

function _createTextBody() {
  const textBody = document.createElement('div')
  textBody.dataset.testid = 'drill-label-text-body'
  textBody.style.backgroundColor = '#222222'
  textBody.style.padding = '0.375em 0.325em'
  return textBody
}

function isDrillLabelContentIdentical(drillLabel: DrillLabelApiData) {
  if (drillLabel.coreImages.length) return false

  const {
    collapsed_intercept_info,
    expanded_intercept_info,
    sub_intercept_info,
    edited_collapsed_intercept_info,
    edited_expanded_intercept_info,
    edited_sub_intercept_info,
  } = drillLabel
  const effectiveValues = [
    edited_collapsed_intercept_info ?? collapsed_intercept_info,
    edited_expanded_intercept_info ?? expanded_intercept_info,
    edited_sub_intercept_info ?? sub_intercept_info,
  ]
  const nonNullValues = effectiveValues.filter(value => value !== null)

  return new Set(nonNullValues).size <= 1
}

function createTextLabelExpand(drillLabel: DrillLabelApiData) {
  const textLabelExpand = document.createElement('span')
  textLabelExpand.dataset.testid = 'drill-label-text-label-expand'
  textLabelExpand.style.padding = '0 1em'
  textLabelExpand.style.fontSize = '1.5em'
  textLabelExpand.style.lineHeight = '1em'

  const shouldDisplayToggleButton =
    !isDrillLabelContentIdentical(drillLabel) && (drillLabel.expanded_intercept_info || drillLabel.sub_intercept_info)

  if (shouldDisplayToggleButton) {
    textLabelExpand.style.cursor = 'pointer'
    const expandCtrl = document.createElement('span')
    expandCtrl.innerHTML = '+'
    expandCtrl.classList.add('collapsed-content')
    const collapseCtrl = document.createElement('span')
    collapseCtrl.innerHTML = '&ndash;'
    collapseCtrl.classList.add('expanded-content')
    textLabelExpand.appendChild(expandCtrl)
    textLabelExpand.appendChild(collapseCtrl)
  }

  return textLabelExpand
}

function createImageContainer(drillLabelData: DrillLabelApiData) {
  const imageContainer = document.createElement('div')

  imageContainer.style.width = '100%'
  imageContainer.style.top = 'calc(100% + 1rem)'
  imageContainer.style.position = 'absolute'
  imageContainer.style.border = '3px solid #222222'
  imageContainer.style.borderRadius = '4px'

  const imageBody = document.createElement('img')
  imageBody.dataset.testid = 'drill-label-image-body'
  imageBody.src = drillLabelData.coreImages[0].image
  imageBody.style.width = '100%'
  imageBody.style.height = 'auto'
  imageBody.setAttribute('draggable', 'false')

  imageContainer.appendChild(imageBody)

  return imageContainer
}

function createLabelDiv() {
  const labelDiv = document.createElement('div')
  labelDiv.dataset.testid = 'drill-label-label-div'
  labelDiv.classList.add('drill-label-label-div')
  labelDiv.style.position = 'absolute'
  labelDiv.style.transformOrigin = '0 0 0'
  labelDiv.style.pointerEvents = 'auto'
  labelDiv.style.transition = `opacity ${DRILL_LABEL_TRANSITION}ms ease-in-out`
  labelDiv.style.minWidth = 'var(--drill-label-width)'
  labelDiv.dataset.threedEntity = 'drill-label'
  return labelDiv
}

function createLabelControls(id: number) {
  const controlContainer = document.createElement('div')
  controlContainer.classList.add('drill-label-controls')
  controlContainer.classList.add(`drill-label-${id}`)
  controlContainer.style.position = 'absolute'
  controlContainer.style.width = '0px'
  controlContainer.style.color = 'white'

  return controlContainer
}

function createSigInterceptControls() {
  const sigInterceptControls = document.createElement('div')

  sigInterceptControls.style.position = 'absolute'
  sigInterceptControls.style.display = 'flex'
  sigInterceptControls.style.justifyContent = 'center'
  sigInterceptControls.style.alignItems = 'center'

  sigInterceptControls.style.fontSize = '1.25em'
  sigInterceptControls.style.height = `${SIG_INTERCEPT_HEIGHT}px`
  sigInterceptControls.style.width = `${SIG_INTERCEPT_WIDTH}px`
  sigInterceptControls.style.borderRadius = '8px'
  sigInterceptControls.style.boxShadow = '1px 2px 1px 0px rgba(116, 116, 116, 0.40)'

  sigInterceptControls.style.cursor = 'pointer'
  sigInterceptControls.style.userSelect = 'none'

  sigInterceptControls.title = 'Spotlight Label'

  sigInterceptControls.innerHTML = getSunSvg()
  sigInterceptControls.classList.add('sig-intercept-controls')

  return sigInterceptControls
}

export class DrillLabelManager implements ThreeDRenderModule, DrillLayerService {
  meshCutoutMaterial = new MeshBasicMaterial({ color: 0x000000 })
  meshCutoutGeometry = rectangleRounded(1, 1, 0.1, 10)
  flagpoleMaterial = new LineBasicMaterial({ color: 0xffffff })
  group: Group = new Group()
  screenWidth: number = 0
  screenHeight: number = 0
  private labels: Map<number, DrillLabelApiData[]> = new Map()
  private oldDrillLabelMap = new Map<number, DrillLabel>()
  private drillLabelMap = new Map<number, DrillLabel>()
  private _container: HTMLElement | null = null
  private modelId: number | null = null
  private drillDataSets: DrillDataSet[] | null = null
  private sceneId: number | null = null
  private sticky: boolean = false
  private stickyCoords: { left?: number; top?: number } = reactive({
    left: 0,
    top: 0,
  })
  private selectedDrillLabelControlsElement: HTMLElement | null = null
  private selectedDrillLabelElement: HTMLElement | null = null
  private intersectionObserver: IntersectionObserver | null = null
  private drillLabelRootSelector = '[data-threed-entity="drill-label"]'
  private textureLoader = new TextureLoader()
  private meshThumbnailMaterial: Material | null = null
  private dataCache: DataCache<DrillLabelConfig[]>

  constructor(
    private mode: 'view' | 'edit',
    private loaderConfig: { uuid: string | null },
    private onLabelsChanged: () => void,
    private onVisibilityChanged?: (payload: DrillLabelVisibility[], source?: 'user' | 'system') => void,
    private onToggleCollapsed?: (payload: { id: number; is_collapsed: boolean }) => void,
    private currentView?: Ref<DeepReadonly<ModelView>>,
    private maxAge: number = 0,
  ) {
    this.meshCutoutMaterial.side = FrontSide
    this.meshCutoutMaterial.opacity = 0
    this.meshCutoutMaterial.transparent = true
    this.meshCutoutMaterial.blending = NoBlending
    this.group.name = 'drill-labels'
    this.meshThumbnailMaterial = createMeshThumbnailMaterial()

    {
      const css = document.styleSheets.item(0)
      const show = 'display: block;'
      const hide = 'display: none;'
      css?.insertRule(`${this.drillLabelRootSelector} .expanded-content { ${hide} }`)
      css?.insertRule(`${this.drillLabelRootSelector} .collapsed-content { ${show} }`)
      css?.insertRule(`${this.drillLabelRootSelector}.expanded .expanded-content { ${show} }`)
      css?.insertRule(`${this.drillLabelRootSelector}.expanded .collapsed-content { ${hide} }`)
      css?.insertRule(`.edit ${this.drillLabelRootSelector} { cursor: move }`)

      css?.insertRule(`${this.drillLabelRootSelector} .sig-intercept-visible {
        outline: 0.2em solid #F5BC22;
        outline-offset: -0.2em;
       }`)

      css?.insertRule(`.drill-label-controls { ${hide} }`)
      css?.insertRule(`.drill-label-controls.controls-visible { ${show} }`)

      css?.insertRule(`.drill-label-controls > .sig-intercept-visible { background: #2F5A62 }`)
      css?.insertRule(`.drill-label-controls > .sig-intercept-controls { background: black }`)

      css?.insertRule(`.drill-label-controls.sticky.controls-visible {
        transition-property: all;
        transition-duration: ${CONTROLS_TRANSITION}ms;
        transition-timing-function: ease-out;
       }`)

      css?.insertRule(`${this.drillLabelRootSelector} .inline-editable {
        display: flex;
        flex-direction: column;
        align-items: center;
      }`)

      css?.insertRule(`${this.drillLabelRootSelector} .inline-editable .clickable-input {
        border-radius: 4px;
        border: 0 solid #b3b3b3;
        padding: 0 0.325em;
        transition: border 0.1s, background 0.2s;
        word-wrap: normal;
        width: 100%;
        white-space: pre-line;
      }`)

      css?.insertRule(`.edit .inline-editable:hover {
        cursor: text;
        background: #4b4b4b;
        outline: 1px solid #b3b3b3;
        border-radius: 4px;
      }`)

      css?.insertRule(`${this.drillLabelRootSelector} .inline-editable textarea {
        resize: none;
        appearance: none;
        background: rgb(34, 34, 34);
        border-radius: 4px;
        outline: 1px solid #b3b3b3;
        color: #fff;
        outline: none;
        line-height: 1.5;
        padding: 0 0.325em;
        min-width: 100%;
        overflow: hidden;
        white-space: nowrap;
        font-size: unset;
      }`)

      this.dataCache = new DataCache<DrillLabelConfig[]>(this.maxAge)
    }
  }

  async fetchLabels({ modelId, drillDataSets }: { modelId: number; drillDataSets: DrillDataSet[] }) {
    this.modelId = modelId
    this.drillDataSets = drillDataSets

    const labelsTuple: [number, DrillLabelApiData[]][] = await Promise.all(
      drillDataSets.map(async dataSet => {
        try {
          const [dataSetId, drillLabels] = await this._getDrillLabelTuple({ drillDataSet: dataSet })
          return [dataSetId, drillLabels]
        } catch (e) {
          return [dataSet.data_set_id, []]
        }
      }),
    )

    if (this.modelId !== modelId) return // avoid race condition

    this.removeDivs()
    this.labels = new Map(labelsTuple)

    if (this.modelId !== modelId) return // avoid race condition

    this.removeDivs()
    this.labels = new Map(labelsTuple)
  }

  async addLabelsForDataSet({ drillDataSet }: { drillDataSet: DrillDataSet }) {
    const [dataSetId, drillLabels] = await this._getDrillLabelTuple({ drillDataSet })
    this.createLabels(dataSetId, drillLabels)
  }

  createLabels(dataSetId: number, drillLabels: DrillLabelApiData[]) {
    if (this.labels.has(dataSetId)) {
      const exisitingLabels = this.labels.get(dataSetId)
      const labels = drillLabels.filter(label => !exisitingLabels?.some(l => l.id === label.id))

      this.labels.set(dataSetId, (exisitingLabels ?? []).concat(labels))
    } else {
      this.labels.set(dataSetId, drillLabels)
    }
    this.createAllDrillLabels()
  }

  getLayerListItems(drillDataSetId: number): DrillLayer[] {
    const labels = this.getLabelsForDrillDataSet(drillDataSetId)
    const drillLayers = this._getDrillLabelLayers(labels.filter(label => 'file' in label))

    return drillLayers
  }

  private _getDrillLabelLayers(labels: DrillLabelApiData[]): DrillLayer[] {
    const holes = groupDataBy(item => item.hole_id, labels)

    return Object.entries(holes).map(([title, labels]) => {
      const files = groupDataBy(item => item.file_id as number, labels as any)
      const groupedFiles = Object.entries(files).map(([fileId, labels]) => {
        const drillLabels = labels.map(label => ({
          id: label.id,
          title: getDrillLabelName(label),
          holeId: label.hole_id,
          visible: this.isVisible(label.id),
        }))

        return {
          fileId,
          title: labels[0].file?.name ?? '',
          visible: drillLabels.some(l => l.visible),
          labels: drillLabels,
        }
      })

      return {
        id: title,
        type: 'drill',
        title,
        visible: groupedFiles.some(f => f.visible),
        files: groupedFiles,
      }
    })
  }

  private async _getDrillLabelTuple({ drillDataSet }: { drillDataSet: DrillDataSet }): Promise<[number, DrillLabelApiData[]]> {
    const [files, coreImages, labels] = await Promise.all([
      $fetch<DrillFile[]>(`/v2/drill/data/${drillDataSet.data_set_id}/files`, {
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
        params: { include: ['labels'] },
      }),
      $fetch<CoreImageApiData[]>(`/v2/drill/data/${drillDataSet.data_set_id}/core_images`, {
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
      }),
      $fetch<DrillLabelApiData[]>(`/v2/drill/data/${drillDataSet.data_set_id}/labels`, {
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
      }),
    ])

    const linkedLabels = labels.filter(apiLabel => files.some(file => file.labels!.some(label => label.id === apiLabel.id)))

    const drillLabelApiDataWithFile = files
      .map(file => {
        return file
          .labels!.filter(label => linkedLabels.some(apiLabel => apiLabel.id === label.id))
          .map(label => {
            const { labels, ...drillFile } = file
            return { file: drillFile, ...label }
          })
      })
      .flat()

    const drillLabels = drillLabelApiDataWithFile
      .map(this.mergeCoreImages(coreImages))
      .map(label => applyLocalization(drillDataSet.localization, label))

    return [drillDataSet.data_set_id, drillLabels]
  }

  private mergeCoreImages(coreImages: CoreImageApiData[]) {
    return (l: DrillLabelApiData) => {
      const coreImage = coreImages.filter(i => i.labels.includes(l.id))

      return {
        ...l,
        coreImages: ([] as CoreImageApiData[]).concat(coreImage),
      }
    }
  }

  removeLabelsForDataSet(id: number) {
    this.removeDivs()

    this.dispose()
    const labelsToDelete = this.labels.get(id)
    labelsToDelete?.forEach(l => this.drillLabelMap.delete(l.id))

    this.labels.delete(id)
    this.createAllDrillLabels()
  }

  async setSceneId(sceneId: number | null) {
    this.sceneId = sceneId

    if (!this.sceneId) {
      this.createAllDrillLabels()
      return
    }

    let configs
    try {
      configs = await this.dataCache.getOrFetch(sceneId as number, async () => {
        return await $fetch<DrillLabelConfig[]>(`/v2/3dmodel/cameras/${sceneId}/drill-labels`, { ...useFetchSharedOptions() })
      })
    } catch {}

    if (this.sceneId !== sceneId || !configs) return

    this.createAllDrillLabels()
    configs.forEach(config => {
      const label = this.getFlattenedLabels().find(({ id }) => config.drill_label_id === id)

      if (!label) return

      const drillLabel = this.getDrillLabelObject(label.id)
      if (!drillLabel) return

      this.toggleCollapsed(label.id, this.isConfigCollapsed(config))

      this.toggleSignificantIntercept(label.id, config.is_significant)

      drillLabel.textLabelContainer.style.fontSize = config.style?.fontSize ?? `${DEFAULT_FONT_SIZE}px`
      drillLabel.fontSize = parseInt(config.style?.fontSize ?? DEFAULT_FONT_SIZE)
      drillLabel.autoScaleSize = config.style?.autoScaleSize ?? DEFAULT_FIXED_SIZE
      drillLabel.auto_scale = config.auto_scale

      const position = new Vector3(label.x, label.y, label.z)
      const offset = new Vector3(config.offset_x, config.offset_y, config.offset_z)

      drillLabel.labelOriginGroup.position.copy(position)
      drillLabel.labelContainerGroup.position.copy(offset)

      // set the flagpole index to be rendered later
      drillLabel.flagpoleIndex = config.flagpole_index
      drillLabel.offset = offset

      this.setVisible(label.id, this.isConfigVisible(config))
    })

    this.onLabelsChanged()
  }

  private isConfigVisible(config: DrillLabelConfig): boolean {
    if (this.currentView?.value.layer_visibilities.length) {
      const visibility = this.currentView.value.layer_visibilities.find(
        v => v.type === 'drill-label' && v.layer_id === `${config.drill_label_id}`,
      )
      if (visibility) return visibility.visible
    }

    return config.is_visible
  }

  private isConfigCollapsed(config: DrillLabelConfig): boolean {
    const currentViewConfig = this.currentView?.value.drill_labels[config.drill_label_id]

    if (currentViewConfig) return currentViewConfig.is_collapsed

    return config.is_collapsed
  }

  isSceneAutoScale(): boolean {
    return this.getFlattenedLabels().some(label => this.getDrillLabelObject(label.id)?.auto_scale)
  }

  isLabelCollapsed(id: number): boolean | undefined {
    const label = this.getDrillLabelObject(id)
    if (!label) return
    return label.is_collapsed
  }

  getDrillLabelObject(id: number) {
    return this.drillLabelMap.get(id)
  }

  getLabelsForDrillDataSet(drillDataSetId: number): DrillLabelApiData[] {
    return this.labels.get(drillDataSetId) ?? []
  }

  getLabel(id: number): DrillLabelApiData | undefined {
    return this.getFlattenedLabels().find(label => label.id === id)
  }

  setFontSize(drillDataSetId: number, fontSize: number): void {
    const labels = this.getLabelsForDrillDataSet(drillDataSetId)

    labels.forEach(label => {
      const drillLabel = this.getDrillLabelObject(label.id)

      if (!drillLabel) return
      if (drillLabel.auto_scale) {
        drillLabel.autoScaleSize = fontSize
      } else {
        drillLabel.textLabelContainer.style.fontSize = `${fontSize}px`
        drillLabel.fontSize = fontSize

        if (drillLabel.apiData.coreImages.length > 0) {
          const svgIcon = drillLabel.textLabelContainer.querySelector('svg')
          svgIcon?.setAttribute('width', `${fontSize}px`)
          svgIcon?.setAttribute('height', `${fontSize}px`)
        }
      }
    })
  }

  setAutoScale(drillDataSetId: number, autoScale: boolean) {
    const labels = this.getLabelsForDrillDataSet(drillDataSetId)

    labels.forEach(label => {
      const drillLabel = this.getDrillLabelObject(label.id)

      if (drillLabel) {
        drillLabel.auto_scale = autoScale
      }
    })
  }

  selectDrillLabel(id: number, { prevSelectedLabelId }: { prevSelectedLabelId?: number }) {
    if (prevSelectedLabelId && prevSelectedLabelId !== id) {
      this.deselectDrillLabel(prevSelectedLabelId)
    }

    const drillLabel = this.getDrillLabelObject(id)
    if (!drillLabel) return

    const { textLabelContainer, controlsDiv } = drillLabel

    textLabelContainer.classList.add('selected')
    controlsDiv.classList.add('controls-visible')

    this.selectedDrillLabelElement = textLabelContainer
    this.selectedDrillLabelControlsElement = controlsDiv

    this.intersectionObserver?.observe(textLabelContainer)
  }

  deselectDrillLabel(id: number) {
    const drillLabel = this.getDrillLabelObject(id)
    if (!drillLabel) return

    const { textLabelContainer, controlsDiv } = drillLabel

    textLabelContainer.classList.remove('selected')
    controlsDiv.classList.remove('controls-visible')

    this.selectedDrillLabelControlsElement = null
    this.selectedDrillLabelElement = null

    this.intersectionObserver?.unobserve(textLabelContainer)
    this.sticky = false
    this.stickyCoords = {}
  }

  resetDrillLabelName(id: number, updates: Partial<DrillLabelApiData> = {}) {
    const drillLabel = this.getDrillLabelObject(id)

    if (!drillLabel) return

    const { labelDiv, apiData } = drillLabel

    Array.from(labelDiv.querySelectorAll('.clickable-input')).forEach(el => {
      if (el.classList.contains('edited_collapsed_intercept_info')) {
        el.textContent = apiData.collapsed_intercept_info ?? ''
      } else if (el.classList.contains('edited_expanded_intercept_info')) {
        el.textContent = apiData.expanded_intercept_info ?? ''
      } else if (el.classList.contains('edited_sub_intercept_info')) {
        el.textContent = apiData.sub_intercept_info ?? ''
      }
    })

    this.updateDrillLabelApiData(id, updates)
  }

  async updateServer(sceneId: number) {
    const labels = clone(this.getFlattenedLabels())
    const serverLabels = await $fetch<DrillLabelConfig[]>(`/v2/3dmodel/cameras/${sceneId}/drill-labels`, { ...useFetchSharedOptions() })
    const drillLabelsToUpdate = await this.getDrillLabelsToUpdate(this.drillDataSets ?? [])

    const toBeAdded = labels.filter(({ id }) => !serverLabels.find(l => l.drill_label_id === id)).map(this.toConfigUpdatePayload.bind(this))
    const toBeUpdated = labels
      .filter(({ id }) => serverLabels.some(l => l.drill_label_id === id))
      .map(this.toConfigUpdatePayload.bind(this))
    const toBeDeleted = serverLabels.filter(({ drill_label_id }) => !labels.find(l => l.id === drill_label_id))

    await Promise.all([
      toBeAdded.length
        ? $fetch<DrillLabelConfig[]>(`/v2/3dmodel/cameras/${sceneId}/drill-labels`, {
            method: 'POST',
            body: toBeAdded,
            ...useFetchSharedOptions(),
          })
        : null,
      toBeUpdated.length
        ? $fetch(`/v2/3dmodel/cameras/${sceneId}/drill-labels`, { method: 'PATCH', body: toBeUpdated, ...useFetchSharedOptions() })
        : null,
      toBeDeleted.length
        ? $fetch(`/v2/3dmodel/cameras/${sceneId}/drill-labels`, { method: 'DELETE', body: toBeDeleted, ...useFetchSharedOptions() })
        : null,
      drillLabelsToUpdate.map(label => {
        return $fetch<DrillLabelApiData>(`/v2/drill/data/${label.apiData.data_set_id}/labels/${label.apiData.id}`, {
          ...useFetchSharedOptions(),
          method: 'PATCH',
          body: {
            edited_collapsed_intercept_info: label.apiData.edited_collapsed_intercept_info,
            edited_expanded_intercept_info: label.apiData.edited_expanded_intercept_info,
            edited_sub_intercept_info: label.apiData.edited_sub_intercept_info,
          },
        })
      }),
    ])
  }

  private async getDrillLabelsToUpdate(drillDataSets: DrillDataSet[]) {
    const serverDrillLabels = await Promise.all(
      drillDataSets.map(dataSet =>
        $fetch<DrillLabelApiData[]>(`/v2/drill/data/${dataSet.data_set_id}/labels`, {
          ...useFetchSharedOptions(),
          headers: {
            ...useFetchSharedOptions().headers,
            'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
          },
        }),
      ),
    )

    const drillLabelValues = Array.from(this.drillLabelMap.values())
    const allServerDrillLabels = serverDrillLabels.flat()

    return drillLabelValues.filter(({ apiData }) => {
      const labels = allServerDrillLabels.filter(l => l.data_set_id === apiData.data_set_id)

      if (!labels) return

      return labels.some(serverLabel => {
        if (serverLabel.id !== apiData.id) return false

        return (
          serverLabel.edited_collapsed_intercept_info !== apiData.edited_collapsed_intercept_info ||
          serverLabel.edited_expanded_intercept_info !== apiData.edited_expanded_intercept_info ||
          serverLabel.edited_sub_intercept_info !== apiData.edited_sub_intercept_info
        )
      })
    })
  }

  private updateDrillLabelApiData(id: number, updates: Partial<DrillLabelApiData> = {}) {
    const drillLabel = this.getDrillLabelObject(id)

    if (!drillLabel) return

    const { apiData } = drillLabel

    const labelData = this.labels.get(apiData.data_set_id)

    if (labelData) {
      this.labels.set(
        apiData.data_set_id,
        labelData.map(label => {
          return label.id === id ? { ...label, ...updates } : label
        }),
      )
    }

    this.drillLabelMap.set(id, {
      ...drillLabel,
      apiData: {
        ...apiData,
        ...updates,
      },
    })
  }

  private toConfigUpdatePayload(value: DrillLabelApiData) {
    const drillLabel = this.getDrillLabelObject(value.id)
    if (!drillLabel) return

    return {
      drill_label_id: value.id,
      flagpole_index: drillLabel.flagpoleIndex,
      is_collapsed: drillLabel.is_collapsed,
      is_visible: drillLabel.labelOriginGroup.visible,
      is_image_collapsed: drillLabel.is_image_collapsed,
      is_significant: drillLabel.is_significant,
      auto_scale: drillLabel.auto_scale,
      style: { fontSize: `${drillLabel.fontSize}px`, autoScaleSize: drillLabel.autoScaleSize },
      offset_x: drillLabel.offset.x,
      offset_y: drillLabel.offset.y,
      offset_z: drillLabel.offset.z,
    }
  }

  render(_: Scene, camera: PerspectiveCamera) {
    const halfWidth = this.screenWidth / 2
    const halfHeight = this.screenHeight / 2

    camera.updateMatrixWorld()
    const fov = camera.projectionMatrix.elements[5] * halfHeight
    const translate = new Matrix4().makeTranslation(halfWidth, halfHeight, 0)
    const perspective = new Matrix4().set(1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 1, fov, 0, 0, -1 / fov, 0)
    perspective.multiply(camera.matrixWorldInverse).premultiply(translate)

    this.getFlattenedLabels().forEach(label => {
      const drillLabel = this.getDrillLabelObject(label.id)
      const transitionLabel = this.oldDrillLabelMap.get(label.id)
      if (drillLabel) {
        this.renderLabel(drillLabel, camera, perspective)
      }
      if (transitionLabel) {
        this.renderLabel(transitionLabel, camera, perspective)
      }
    })
  }

  mount(container: HTMLElement) {
    this._container = container

    this._container.style.setProperty('--drill-label-width', 'auto')

    let options = {
      root: container,
      // -127px is derived from height of title overlay, significant intercept button height, and the margins
      rootMargin: '-127px -10px 0px 0px',
      threshold: 0.99,
    }

    this.intersectionObserver = new IntersectionObserver((entries, observer) => {
      if (!this.selectedDrillLabelElement || !this.selectedDrillLabelControlsElement) return

      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          const titleOverlayPlusOffset = TITLE_OVERLAY_HEIGHT + 8 // 8px is the offset between title overlay and controls

          this.selectedDrillLabelControlsElement?.classList.add('sticky')

          this.sticky = true
          this.stickyCoords = {
            left: container.getBoundingClientRect().width / 2 + container.getBoundingClientRect().left,
            top: container.getBoundingClientRect().top + titleOverlayPlusOffset,
          }
        } else {
          this.sticky = false
          this.stickyCoords = {}

          setTimeout(() => {
            // allows the transition to occur to move the button back to original position
            this.selectedDrillLabelControlsElement?.classList.remove('sticky')
          }, CONTROLS_TRANSITION)
        }
      })
    }, options)
  }

  setSize(width: number, height: number) {
    this.screenWidth = width
    this.screenHeight = height
  }

  resetData() {
    this.removeDivs()
    this.disposeTransitionDrillLabels()
    this.dispose()
    this.labels = new Map()
    this.drillLabelMap = new Map()
    this.modelId = null
    this.dataCache.clear()
  }

  private removeDivs(): void {
    this.getFlattenedLabels().forEach(label => {
      const {
        textLabelContainer,
        labelDiv,
        sigInterceptControls,
        textGroup,
        imageGroup,
        controlsDiv,
        textLabelResizeObserver,
        imageContainerResizeObserver,
      } = this.getDrillLabelObject(label.id) ?? {}

      textLabelResizeObserver?.disconnect()
      imageContainerResizeObserver?.disconnect()
      textGroup?.remove()
      imageGroup?.remove()
      sigInterceptControls?.remove()
      controlsDiv?.remove()
      setTimeout(() => {
        // fade out before removing div
        if (labelDiv) {
          labelDiv.style.transition = `opacity ${DRILL_LABEL_TRANSITION} ease-in-out`
          labelDiv.style.opacity = '0%'
        }
        setTimeout(() => {
          textLabelContainer?.remove()
          labelDiv?.remove()
        }, DRILL_LABEL_TRANSITION)
      }, DRILL_LABEL_TRANSITION)
    })
  }

  private getMinDistance(position: Vector3): number {
    let minDistance = Infinity
    this.group.children.forEach(child => {
      const distance = child.position.distanceTo(position)
      if (distance < minDistance) {
        minDistance = distance
      }
    })
    return minDistance
  }

  private calculateDefaultOffset(position: Vector3): Vector3 {
    const defaultOffset = new Vector3(150, 100, 0)
    const thresholdDistance = 200
    const maxIterations = 20

    let offset = defaultOffset.clone()
    let iterations = 1
    // this calculation reduces instances of overlapping labels on import
    while (this.getMinDistance(position.clone().add(offset)) < thresholdDistance && iterations < maxIterations) {
      offset = defaultOffset.clone().multiplyScalar(1 + iterations / 5)
      offset.applyAxisAngle(new Vector3(0, 0, 1), (-Math.PI / 2) * iterations)
      iterations++
    }
    return offset
  }

  hideLabels() {
    this.removeDivs()
    this.oldDrillLabelMap.forEach(child => {
      child.labelOriginGroup.visible = false
    })
  }

  createAllDrillLabels() {
    this._container?.style.setProperty('--drill-label-width', 'auto')
    this.disposeTransitionDrillLabels()
    this.oldDrillLabelMap = this.drillLabelMap
    this.hideLabels()
    this.drillLabelMap = new Map()

    for (const drillLabelData of this.getFlattenedLabels()) {
      const labelDiv = createLabelDiv()
      labelDiv.style.opacity = '0%'
      this._container?.appendChild(labelDiv)

      const offset = this.calculateDefaultOffset(new Vector3(drillLabelData.x, drillLabelData.y, drillLabelData.z))
      const position = new Vector3(drillLabelData.x, drillLabelData.y, drillLabelData.z)

      const flagpoleGeometry = new BufferGeometry()
      const flagpole = new Line(flagpoleGeometry, this.flagpoleMaterial)

      const points = [new Vector3(0, 0, 0), offset.clone().multiplyScalar(-1)]
      flagpoleGeometry.setFromPoints(points)
      flagpoleGeometry.setDrawRange(0, 2)

      const labelOriginGroup = new Group() // flagpole, labelcontainer, and labelorientationhelper
      labelOriginGroup.position.copy(position)
      labelOriginGroup.add(flagpole)
      labelOriginGroup.name = drillLabelData.hole_id
      labelOriginGroup.userData.drillLabelId = drillLabelData.id
      labelOriginGroup.visible = false

      const labelOrientationHelper = new Group()
      labelOrientationHelper.name = 'orientation helper'
      labelOriginGroup.add(labelOrientationHelper)

      const labelContainerGroup = new Group()
      labelContainerGroup.name = 'label container'
      labelContainerGroup.position.copy(offset)
      labelOriginGroup.add(labelContainerGroup)

      labelDiv.style.display = 'none'

      const textGroup = new Group()
      textGroup.name = 'text'
      labelContainerGroup.add(textGroup)

      const imageGroup = new Group()
      imageGroup.name = 'image'
      labelContainerGroup.add(imageGroup)

      const textLabelContainer = createTextLabel()
      const textLabelExpand = createTextLabelExpand(drillLabelData)
      const labelControls = createLabelControls(drillLabelData.id)
      const sigInterceptControls = createSigInterceptControls()

      const textLabelResizeObserver = new ResizeObserver(([el]) => {
        const width = (el.target as HTMLElement).offsetWidth
        const height = (el.target as HTMLElement).offsetHeight

        if (width === 0 || height === 0) return

        // Hide to prevent jumping labels while we reposition
        labelDiv.style.visibility = 'hidden'
        const drillLabel = this.drillLabelMap.get(drillLabelData.id)

        textGroup.traverse(child => {
          if (child instanceof Mesh) {
            textGroup.remove(child)
            child.geometry.dispose()
          }
        })

        const newGeometry = rectangleRounded(width, height, DEFAULT_FONT_SIZE / 4, 10)
        const mesh = new Mesh(newGeometry, this.meshCutoutMaterial)
        textGroup.add(mesh)
        newGeometry.computeBoundingBox()

        if (drillLabel) {
          const vertexAttachment = getVertex(mesh, drillLabel.flagpoleIndex).sub(drillLabel.labelOriginGroup.position)
          const points = [new Vector3(0, 0, 0), vertexAttachment]
          drillLabel.flagpole.geometry.setFromPoints(points)
          drillLabel.flagpole.geometry.computeBoundingSphere()
          drillLabel.labelOrientationHelper.position.copy(vertexAttachment)

          this.drillLabelMap.set(drillLabelData.id, {
            ...drillLabel,
            mesh,
          })
        }

        // this resize observer is running even for labels that are not visible in the scene
        // this should be corrected
        const currentWidth = parseInt(this._container!.style.getPropertyValue('--drill-label-width')) || 0
        const newWidth = Math.max(currentWidth, labelDiv.offsetWidth)

        this._container?.style.setProperty('--drill-label-width', `${newWidth}px`)

        labelControls.style.left = `${labelDiv.getBoundingClientRect().right - SIG_INTERCEPT_WIDTH}px`

        setTimeout(() => {
          labelDiv.style.opacity = '100%'
          labelDiv.style.visibility = 'visible'
        }, DRILL_LABEL_FADE_IN_DELAY)
        this.onLabelsChanged()
      })

      sigInterceptControls.addEventListener('click', () => {
        this.toggleSignificantIntercept(drillLabelData.id)
      })

      if (!isDrillLabelContentIdentical(drillLabelData)) {
        textLabelExpand.addEventListener('click', () => {
          const drillLabel = this.getDrillLabelObject(drillLabelData.id)
          if (!drillLabel) return

          return this.toggleCollapsed(drillLabelData.id, !drillLabel.is_collapsed)
        })
      }

      const textTitle = createTextTitle(drillLabelData)
      textTitle.appendChild(textLabelExpand)
      labelDiv.appendChild(textLabelContainer)
      textLabelContainer.appendChild(textTitle)

      const collapsedTextBody = createCollapsedTextBody.call(this, drillLabelData)
      textLabelContainer.appendChild(collapsedTextBody)
      collapsedTextBody.dataset.drillId = drillLabelData.id.toString()

      const fullTextBody = createFullTextBody.call(this, drillLabelData)
      textLabelContainer.appendChild(fullTextBody)
      fullTextBody.classList.add('expanded-content')
      fullTextBody.dataset.drillId = drillLabelData.id.toString()

      labelControls.appendChild(sigInterceptControls)
      // attached to document.body for z-index purposes and keeping drill-label-manager clueless of external elements
      document.body.appendChild(labelControls)

      let imageContainer: HTMLElement

      const imageContainerResizeObserver = new ResizeObserver(([el]) => {
        const width = (el.target as HTMLElement).offsetWidth
        const height = (el.target as HTMLElement).offsetHeight

        if (width === 0 || height === 0) {
          imageGroup.visible = false
          this.onLabelsChanged()
          return
        }

        imageGroup.visible = true

        // Hide to prevent jumping labels while we reposition
        imageContainer.style.opacity = '0%'
        const drillLabel = this.drillLabelMap.get(drillLabelData.id)

        imageGroup.traverse(child => {
          if (child instanceof Mesh) {
            imageGroup.remove(child)
            child.geometry.dispose()
          }
        })

        const newGeometry = rectangleRounded(width, height, DEFAULT_FONT_SIZE / 4, 10)
        const mesh = new Mesh(newGeometry, this.meshCutoutMaterial)
        imageGroup.add(mesh)
        newGeometry.computeBoundingBox()

        if (drillLabel?.mesh) {
          imageGroup.position.y = drillLabel.mesh.geometry.boundingBox!.min.y - 16
        }

        setTimeout(() => {
          imageContainer.style.opacity = '100%'
        }, DRILL_LABEL_FADE_IN_DELAY)
        this.onLabelsChanged()
      })

      textLabelResizeObserver.observe(textLabelContainer)

      if (drillLabelData.coreImages[0]) {
        imageContainer = createImageContainer(drillLabelData)
        imageContainer.classList.add('expanded-content')
        textLabelContainer.appendChild(imageContainer)
        imageContainerResizeObserver.observe(imageContainer)
      }

      this.group.add(labelOriginGroup)
      this.drillLabelMap.set(drillLabelData.id, {
        flagpoleIndex: 7, // temp until flagpole is persisted properly
        textLabelContainer,
        labelDiv,
        textGroup,
        imageGroup,
        controlsDiv: labelControls,
        sigInterceptControls,
        apiData: drillLabelData,
        labelContainerGroup,
        labelOrientationHelper,
        flagpole,
        offset,
        labelOriginGroup,
        textLabelResizeObserver,
        imageContainerResizeObserver,
        is_collapsed: true,
        is_image_collapsed: true,
        is_significant: false,
        auto_scale: false, // default to false until an auto_scale dragging solution is found
        fontSize: DEFAULT_FONT_SIZE,
        autoScaleSize: DEFAULT_FIXED_SIZE,
      })
    }
    this.onLabelsChanged()
  }

  getWidestDrillLabelWidth() {
    return this.getFlattenedLabels().reduce((width, label) => {
      const drillLabel = this.getDrillLabelObject(label.id)

      if (!drillLabel) return width

      const textLabel = drillLabel.textLabelContainer
      const textLabelWidth = textLabel.offsetWidth

      return textLabelWidth > width ? textLabelWidth : width
    }, 0)
  }

  toggleCollapsed(id: number, collapsed: boolean) {
    const drillLabel = this.getDrillLabelObject(id)
    if (!drillLabel) return

    const { labelDiv } = drillLabel

    drillLabel.is_collapsed = collapsed
    drillLabel.is_image_collapsed = collapsed

    if (collapsed) {
      labelDiv.classList.remove('expanded')
    } else {
      labelDiv.classList.add('expanded')
    }

    if (this.getWidestDrillLabelWidth() > 0) {
      this._container?.style.setProperty('--drill-label-width', `${this.getWidestDrillLabelWidth()}px`)
    }

    this.onToggleCollapsed?.({ id, is_collapsed: collapsed })
  }

  toggleAllCollapsed(dataSetId: number): void {
    const labels = this.getLabelsForDrillDataSet(dataSetId)
    const isAnyExpanded = labels.some(label => !this.isLabelCollapsed(label.id))
    if (isAnyExpanded) {
      labels.forEach(label => {
        this.toggleCollapsed(label.id, true)
      })
    } else {
      labels.forEach(label => {
        this.toggleCollapsed(label.id, false)
      })
    }
  }

  private toggleSignificantIntercept(id: number, significant?: boolean) {
    const drillLabel = this.getDrillLabelObject(id)
    const newValue = significant === undefined ? !drillLabel?.is_significant : significant

    if (!drillLabel) return

    if (newValue) {
      drillLabel.is_significant = newValue
      drillLabel.sigInterceptControls.classList.add('sig-intercept-visible')
      drillLabel.textLabelContainer.classList.add('sig-intercept-visible')
    } else {
      drillLabel.is_significant = newValue
      drillLabel.sigInterceptControls.classList.remove('sig-intercept-visible')
      drillLabel.textLabelContainer.classList.remove('sig-intercept-visible')
    }
  }

  isVisible(id: number): boolean {
    const drillLabel = this.getDrillLabelObject(id)

    if (!drillLabel) return false

    return drillLabel.labelOriginGroup.visible
  }

  setDataSetVisibility(dataSetId: number, visible: boolean) {
    const labels = this.getLabelsForDrillDataSet(dataSetId)
    labels.forEach(label => {
      this.setVisible(label.id, visible, 'user')
    })
  }

  setVisible(id: number, visible: boolean, source?: 'user' | 'system') {
    const label = this.getFlattenedLabels().find(label => label.id === id)

    if (!label) return

    const drillLabel = this.getDrillLabelObject(label.id)
    if (!drillLabel) return

    const currentlyVisible = drillLabel.labelOriginGroup.visible
    if (visible === currentlyVisible) return

    drillLabel.labelOriginGroup.visible = visible
    if (visible) {
      drillLabel.labelDiv.style.display = 'unset'
    } else {
      drillLabel.labelDiv.style.display = 'none'
      this.deselectDrillLabel(id)
    }

    this.onLabelsChanged()
    this.onVisibilityChanged?.([{ drillLabelId: id, visible }], source)
  }

  private getFlattenedLabels(): DrillLabelApiData[] {
    return Array.from(this.labels.values()).flat()
  }

  renderLabel(drillLabel: DrillLabel, camera: PerspectiveCamera, perspective: Matrix4) {
    const position = new Vector3()
    const scale = new Vector3()

    const {
      labelContainerGroup,
      labelOrientationHelper,
      labelOriginGroup,
      textGroup,
      labelDiv,
      controlsDiv,
      auto_scale,
      fontSize,
      autoScaleSize,
    } = drillLabel

    labelOrientationHelper.quaternion.copy(camera.quaternion)

    const obj = new Object3D()

    labelContainerGroup.position.copy(drillLabel.offset)

    if (drillLabel.mesh) {
      const positionAttributes = drillLabel.mesh.geometry.getAttribute('position') as BufferAttribute
      const positions = positionAttributes.array

      const vertexInLocalSpace = new Vector3().fromArray(positions, drillLabel!.flagpoleIndex * 3)
      vertexInLocalSpace.multiplyVectors(vertexInLocalSpace, labelOrientationHelper.scale)
      vertexInLocalSpace.applyQuaternion(labelOrientationHelper.quaternion)
      const vertexInContainerSpace = new Vector3().addVectors(vertexInLocalSpace, labelContainerGroup.position)

      const rotationOffset = new Vector3().subVectors(vertexInContainerSpace, labelOrientationHelper.position)

      labelContainerGroup.quaternion.copy(labelOrientationHelper.quaternion)
      labelContainerGroup.scale.copy(labelOrientationHelper.scale)
      labelContainerGroup.position.sub(rotationOffset)
    }

    obj.position.copy(position)
    obj.updateMatrixWorld()
    obj.matrixWorld.decompose(position, new Quaternion(), scale)

    textGroup.getWorldPosition(position)
    const matrix = camera.matrixWorldInverse.clone().transpose()

    // styling for divs
    matrix.setPosition(position)
    matrix.scale(scale)

    // remove last row data if any
    matrix.elements[3] = 0
    matrix.elements[7] = 0
    matrix.elements[11] = 0
    matrix.elements[15] = 1

    // flip y axis
    const _m3 = new Matrix4().identity()
    _m3.elements[5] = -1

    matrix.multiply(_m3)

    if (auto_scale) {
      const scaleAmount = this.fixedLabelSize(labelOriginGroup, labelOrientationHelper, camera, autoScaleSize, fontSize)
      const _a = new Vector3().set(1, 1, 1)
      matrix.scale(_a.multiplyScalar(scaleAmount))
    } else {
      labelOrientationHelper.scale.set(1, 1, 1)
    }

    // mix in camera world matrix
    matrix.premultiply(perspective)

    // remove any z-shearing
    matrix.elements[8] = 0
    matrix.elements[9] = 0
    matrix.elements[10] = 1
    matrix.elements[11] = 0

    labelDiv.style.transform = this.getCSSMatrix(matrix)

    if (this.sticky) {
      controlsDiv.style.top = `${this.stickyCoords.top!}px`
      controlsDiv.style.left = `${this.stickyCoords.left!}px`
    } else {
      const interceptHeightPlusOffset = SIG_INTERCEPT_HEIGHT + 8 // 8px is the offset between label and controls
      controlsDiv.style.top = `${labelDiv.getBoundingClientRect().top - interceptHeightPlusOffset}px`
      controlsDiv.style.left = `${labelDiv.getBoundingClientRect().right - SIG_INTERCEPT_WIDTH}px`
    }
  }

  fixedLabelSize(labelOriginGroup: Group, labelOrientationHelper: Group, camera: Camera, autoScaleSize: number, textSize: number): number {
    let distance = camera.position.distanceTo(labelOriginGroup.position)

    labelOrientationHelper.scale.x = (distance * autoScaleSize) / (2e4 * textSize)
    labelOrientationHelper.scale.y = (distance * autoScaleSize) / (2e4 * textSize)

    return (distance * autoScaleSize) / (2e4 * textSize)
  }

  epsilon(value: number): number {
    return Math.abs(value) < 1e-10 ? 0 : value
  }

  getCSSMatrix(matrix: Matrix4): string {
    return `matrix3d(${matrix.elements.map(this.epsilon).join(', ')})`
  }

  dispose() {
    this.drillLabelMap.forEach(child => {
      child.labelOriginGroup.parent?.remove(child.labelOriginGroup)
      if (child.textGroup.children[0] instanceof Mesh) {
        child.textGroup.children[0].geometry.dispose()
      }

      if (child.flagpole instanceof Line) {
        child.flagpole.geometry.dispose()
      }
    })
  }

  disposeTransitionDrillLabels() {
    this.oldDrillLabelMap.forEach(child => {
      child.labelOriginGroup.parent?.remove(child.labelOriginGroup)
      if (child.textGroup.children[0] instanceof Mesh) {
        child.textGroup.children[0].geometry.dispose()
      }

      if (child.flagpole instanceof Line) {
        child.flagpole.geometry.dispose()
      }
      if (child.labelDiv) {
        child.labelDiv.style.transition = 'none' // prevents ghosting when interrupting transitions
      }
    })
  }

  async applyThumbnailMaterial() {
    const texturePromises: Array<Promise<Texture | void>> = []
    this.drillLabelMap.forEach(drillLabel => {
      drillLabel.textGroup.traverse(child => {
        if (child instanceof Mesh) {
          child.material.dispose()
          child.material = this.meshThumbnailMaterial
          child.material.needsUpdate = true
        }
      })

      if (drillLabel.apiData.coreImages.length > 0 && !drillLabel.is_image_collapsed) {
        texturePromises.push(
          this.textureLoader.loadAsync(drillLabel.apiData.coreImages[0].image).then(texture => {
            drillLabel.imageGroup.traverse(child => {
              if (child instanceof Mesh) {
                child.material.dispose()
                child.material = new MeshBasicMaterial({ map: texture })
                child.material.needsUpdate = true
              }
            })
          }),
        )
      }
    })
    return Promise.all(texturePromises)
  }

  applyCutoutMaterial() {
    this.drillLabelMap.forEach(drillLabel => {
      drillLabel.textGroup.traverse(child => {
        if (child instanceof Mesh) {
          child.material.dispose()
          child.material = this.meshCutoutMaterial
          child.material.needsUpdate = true
        }
      })

      drillLabel.imageGroup.traverse(child => {
        if (child instanceof Mesh) {
          child.material.dispose()
          child.material = this.meshCutoutMaterial
          child.material.needsUpdate = true
        }
      })
    })
  }

  setMode(mode: 'view' | 'edit') {
    this.mode = mode

    const labelContainer = document.querySelector('.label-container')

    if (!labelContainer) return

    const css = document.styleSheets[0] as CSSStyleSheet

    if (mode === 'edit') {
      css?.insertRule(`${this.drillLabelRootSelector} .selected {
        outline: 2px solid #6EE7B7;
        outline-offset: -2px;
      }`)

      labelContainer.classList.add('edit')
    } else {
      labelContainer.classList.remove('edit')
    }
  }
}

function getSunSvg() {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M11.5262 2.33943C12.1613 2.33943 12.6762 2.8543 12.6762 3.48943V4.63943C12.6762 5.27456 12.1613 5.78943 11.5262 5.78943C10.891 5.78943 10.3762 5.27456 10.3762 4.63943V3.48943C10.3762 2.8543 10.891 2.33943 11.5262 2.33943Z" fill="white"/>
    <path d="M16.1262 11.5394C16.1262 14.0799 14.0667 16.1394 11.5262 16.1394C8.98566 16.1394 6.92617 14.0799 6.92617 11.5394C6.92617 8.99892 8.98566 6.93943 11.5262 6.93943C14.0667 6.93943 16.1262 8.99892 16.1262 11.5394Z" fill="white"/>
    <path d="M15.5921 17.2316L16.4053 18.0448C16.8544 18.4939 17.5825 18.4939 18.0316 18.0448C18.4807 17.5957 18.4807 16.8675 18.0316 16.4184L17.2184 15.6052C16.7693 15.1561 16.0412 15.1561 15.5921 15.6052C15.143 16.0544 15.143 16.7825 15.5921 17.2316Z" fill="white"/>
    <path d="M18.0315 5.03399C18.4806 5.48309 18.4806 6.21123 18.0315 6.66033L17.2183 7.4735C16.7692 7.92261 16.0411 7.92261 15.592 7.4735C15.1429 7.0244 15.1429 6.29626 15.592 5.84716L16.4052 5.03399C16.8543 4.58488 17.5824 4.58488 18.0315 5.03399Z" fill="white"/>
    <path d="M19.5762 12.6894C20.2113 12.6894 20.7262 12.1746 20.7262 11.5394C20.7262 10.9043 20.2113 10.3894 19.5762 10.3894H18.4262C17.791 10.3894 17.2762 10.9043 17.2762 11.5394C17.2762 12.1746 17.791 12.6894 18.4262 12.6894H19.5762Z" fill="white"/>
    <path d="M11.5262 17.2894C12.1613 17.2894 12.6762 17.8043 12.6762 18.4394V19.5894C12.6762 20.2246 12.1613 20.7394 11.5262 20.7394C10.891 20.7394 10.3762 20.2246 10.3762 19.5894V18.4394C10.3762 17.8043 10.891 17.2894 11.5262 17.2894Z" fill="white"/>
    <path d="M5.83403 7.47352C6.28313 7.92263 7.01127 7.92263 7.46038 7.47352C7.90948 7.02442 7.90948 6.29628 7.46038 5.84718L6.6472 5.03401C6.1981 4.5849 5.46996 4.5849 5.02086 5.03401C4.57175 5.48311 4.57175 6.21125 5.02086 6.66035L5.83403 7.47352Z" fill="white"/>
    <path d="M7.46027 17.2316L6.6471 18.0447C6.19799 18.4938 5.46985 18.4938 5.02075 18.0447C4.57165 17.5956 4.57165 16.8675 5.02075 16.4184L5.83392 15.6052C6.28303 15.1561 7.01117 15.1561 7.46027 15.6052C7.90937 16.0543 7.90937 16.7825 7.46027 17.2316Z" fill="white"/>
    <path d="M4.62617 12.6894C5.2613 12.6894 5.77617 12.1746 5.77617 11.5394C5.77617 10.9043 5.2613 10.3894 4.62617 10.3894H3.47617C2.84104 10.3894 2.32617 10.9043 2.32617 11.5394C2.32617 12.1746 2.84104 12.6894 3.47617 12.6894H4.62617Z" fill="white"/>
    </svg>`
}

function getImageSvg() {
  return `
    <svg height="100%" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M1.37846 0.789062C0.715724 0.789062 0.178467 1.32632 0.178467 1.98906V7.98906C0.178467 8.6518 0.715724 9.18906 1.37846 9.18906H8.57845C9.24119 9.18906 9.77845 8.6518 9.77845 7.98906V1.98906C9.77845 1.32632 9.24119 0.789062 8.57845 0.789062H1.37846ZM8.57845 7.98906H1.37846L3.77846 3.18906L5.57845 6.78906L6.77845 4.38906L8.57845 7.98906Z" fill="white"/>
    </svg>
  `
}
